/**
 * ComplianceRecipients — User Management page listing the users who
 * receive event-based compliance alert emails. Admins pick from the
 * existing AMC users list; duplicates are silently deduped by the
 * backend.
 *
 * Endpoints used:
 *   GET    /api/compliance-recipients
 *   POST   /api/compliance-recipients   { user_ids: [...] }
 *   DELETE /api/compliance-recipients/{user_id}
 *   GET    /api/users
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ShieldCheck, Trash2, Plus, X, Search } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ComplianceRecipients = () => {
  const [recipients, setRecipients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rec, users] = await Promise.all([
        axios.get(`${API_URL}/compliance-recipients`),
        axios.get(`${API_URL}/users`),
      ]);
      setRecipients(rec.data.recipients || []);
      // Users endpoint sometimes returns {users: [...]} vs a bare array.
      setAllUsers(Array.isArray(users.data) ? users.data : (users.data.users || []));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentIds = useMemo(
    () => new Set((recipients || []).map((r) => r.user_id)),
    [recipients],
  );
  const candidates = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (allUsers || [])
      .filter((u) => !currentIds.has(u.id) && u.is_active !== false)
      .filter((u) => !query
        || (u.name || '').toLowerCase().includes(query)
        || (u.email || '').toLowerCase().includes(query));
  }, [allUsers, currentIds, q]);

  const toggleSelected = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const add = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/compliance-recipients`, {
        user_ids: Array.from(selected),
      });
      setSelected(new Set());
      setPicker(false);
      setQ('');
      await load();
    } catch (e) {
      alert(`Failed to add: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (uid) => {
    if (!window.confirm('Remove this user from Compliance Recipients?')) return;
    try {
      await axios.delete(`${API_URL}/compliance-recipients/${uid}`);
      await load();
    } catch (e) {
      alert(`Failed to remove: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="compliance-recipients-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <ShieldCheck size={22} className="text-blue-600" />
            Compliance Recipients
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Users listed here receive automated compliance alert emails
            (e.g. Form InVI filing reminders) whenever the corresponding
            event fires on an investor account that has the rule turned ON.
          </p>
        </div>
        <button
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
          onClick={() => setPicker(true)}
          data-testid="add-recipient-btn"
        >
          <Plus size={16} /> Add Users
        </button>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="recipients-table">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Added</th>
              <th className="text-right px-4 py-2 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-6 text-gray-400">Loading…</td></tr>
            ) : recipients.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-gray-500" data-testid="empty-state">
                No compliance recipients configured. Click <b>Add Users</b> to pick from your team.
              </td></tr>
            ) : recipients.map((r) => (
              <tr key={r.user_id} className="border-t hover:bg-gray-50" data-testid={`recipient-row-${r.user_id}`}>
                <td className="px-4 py-2 font-medium text-gray-800">{r.name || '—'}</td>
                <td className="px-4 py-2 text-gray-600">{r.email || '—'}</td>
                <td className="px-4 py-2 text-gray-600">{r.role || '—'}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">
                  {r.added_at ? new Date(r.added_at).toLocaleString() : '—'}
                  {r.added_by && <div className="text-gray-400">by {r.added_by}</div>}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    onClick={() => remove(r.user_id)}
                    data-testid={`remove-${r.user_id}`}
                    title="Remove"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {picker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" data-testid="picker-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold">Add Compliance Recipients</h3>
              <button onClick={() => { setPicker(false); setSelected(new Set()); setQ(''); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name or email…"
                  className="w-full border rounded pl-8 pr-3 py-1.5 text-sm"
                  data-testid="picker-search"
                />
              </div>
              <div className="max-h-72 overflow-y-auto border rounded">
                {candidates.length === 0 ? (
                  <p className="text-sm text-gray-500 p-4 text-center">
                    {allUsers.length === 0 ? 'Loading users…' : 'No matching users.'}
                  </p>
                ) : candidates.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 px-3 py-2 border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    data-testid={`picker-opt-${u.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleSelected(u.id)}
                      className="rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{u.name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email} · {u.role}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50">
              <button
                onClick={() => { setPicker(false); setSelected(new Set()); setQ(''); }}
                className="text-sm px-3 py-1.5 border rounded hover:bg-white"
              >Cancel</button>
              <button
                onClick={add}
                disabled={busy || selected.size === 0}
                className="text-sm px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                data-testid="picker-submit"
              >
                {busy ? 'Adding…' : `Add ${selected.size || ''}`.trim()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceRecipients;
